/*LIBRARY*/
import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";

/*COMPONENT*/
import Modal from "../../Components/common_modal/Modal";
import SpinnerSimpleBlue from "../../Components/common_spinner/SpinnerSimpleBlue";
import ICON_ARROW_LEFT_LINE from "../../Assets/jsx/ICON_ARROW_LEFT_LINE";

/*REDUX*/
import {
  resetSearchUser,
  searchCollaborator,
  set_value_user,
} from "../../App/actions/authActions";
import {
  getGroup,
  deleteMember,
  editMember,
  searchUser,
  addMember,
} from "../../App/actions/groupActions";
import { invitations } from "../../App/actions/layerNewActions";
import { set_value_project } from "../../App/actions/projectActions";
import {
  block_license_with_handler,
  get_payment_id_from_license,
  is_pass_license_sharing,
} from "../../App/actions/filter_license";

/*PICTURE*/
import pic_static from "../../Assets/svg/profile_pic_comment.svg";
import checklist from "../../Assets/svg/checklist.svg";
import icon_search from "../../Assets/svg/icon_search.svg";

/*FUNCTION*/
import isEmpty from "../../App/validation/is-empty";

/*DATA*/
import dict from "../../Data/dict.json";

/*CONST*/
const WAIT_INTERVAL = 1000;
const ENTER_KEY = 13;

// --- SIMPLE SVG ICONS ---
const SortIconUp = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

const SortIconDown = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const GroupDetail = ({
  auth,
  getGroup,
  deleteMember,
  editMember,
  searchUser,
  resetSearchUser,
  addMember,
  set_value_user,
  set_value_project,
  searchCollaborator,
  is_pass_license_sharing,
  get_payment_id_from_license,
}) => {
  const { id } = useParams();
  const timer = useRef(null);

  const [modalDeleteMember, setModalDeleteMember] = useState(false);
  const [member, setMember] = useState({});
  const [search_user, setSearchUser] = useState("");

  // Sorting state: Default is key: null (which means sorted by original index/number)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const { user, group, loadingDetail, user_list } = auth;

  useEffect(() => {
    // 1. Scroll to top on mount
    window.scrollTo(0, 0);

    getGroup(id);

    return () => {
      set_value_user("group", {});
      set_value_project({
        key: "folder_detail",
        value: {},
      });
      if (timer.current) clearTimeout(timer.current);
    };
  }, [id, getGroup, set_value_user, set_value_project]);

  const triggerChange = () => {
    const payment_id = get_payment_id_from_license();
    try {
      let body = { search_user, payment_id };
      if (!group.is_external) {
        searchCollaborator(body);
      } else {
        searchUser(search_user);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchUser(value);

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      triggerChange();
    }, WAIT_INTERVAL);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === ENTER_KEY) {
      triggerChange();
    }
  };

  const handleResetSearchUser = () => {
    setSearchUser("");
    resetSearchUser();
  };

  const toggleDeleteMember = (selectedMember) => {
    if (selectedMember && selectedMember.user) {
      setMember(selectedMember);
    }
    setModalDeleteMember(!modalDeleteMember);
  };

  const handleDeleteMember = () => {
    if (member._id) {
      const body = {
        group_id: id,
        member_id: member._id,
        member_name: member?.user?.full_name
          ? member?.user?.full_name
          : member?.user?.name,
      };
      deleteMember(body);
    }
    setModalDeleteMember(false);
  };

  const handleEditMember = (e) => {
    e.preventDefault();
    const status = e.target.value;
    const member_id = e.target.name;
    const member_user_id = e.target.id;
    const body = {
      group_id: group._id,
      member_id,
      member_user_id,
      status,
    };
    editMember(body);
  };

  const onAddMember = (member_id) => {
    handleResetSearchUser();
    if (is_pass_license_sharing()) {
      const body = {
        group_id: id,
        member_id,
      };
      addMember(body);
    } else {
      set_value_user("modal_payment_universal", true);
    }
  };

  // --- SORTING FUNCTIONS ---

  // Sort by toggle (Name or Role)
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sort to original array (reset) - effectively sorting by "No."
  const resetSort = () => {
    setSortConfig({ key: null, direction: "asc" });
  };

  // local storage
  const language = localStorage?.language || "ina";

  // content
  let group_members = !isEmpty(group?.members) ? group?.members : [];
  group_members = group_members.filter((m) => m?.user?._id);

  // --- SORTING LOGIC START ---
  // 1. Separate Creator(s) from the rest
  const creators = group_members.filter((m) => m?.status === "creator");
  let others = group_members.filter((m) => m?.status !== "creator");

  // 2. Sort the "others" array based on configuration
  if (sortConfig.key !== null) {
    others.sort((a, b) => {
      let aValue = "";
      let bValue = "";

      if (sortConfig.key === "name") {
        aValue = (a?.user?.full_name || a?.user?.name || "").toLowerCase();
        bValue = (b?.user?.full_name || b?.user?.name || "").toLowerCase();
      } else if (sortConfig.key === "role") {
        aValue = (a?.status || "").toLowerCase();
        bValue = (b?.status || "").toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  // 3. Recombine: Creator always first + sorted others
  let sorted_members = [...creators, ...others];
  // --- SORTING LOGIC END ---

  let itemContent;
  if (loadingDetail) {
    itemContent = (
      <SpinnerSimpleBlue
        width={200}
        unik="loading_item_list"
        marginTop="0px"
        center={true}
        border={0.1}
      />
    );
  } else {
    const members_group_id = sorted_members.map((m) => m?.user?._id);
    let user_list_content;
    if (user_list.length > 0) {
      user_list_content = (
        <section>
          {user_list.map(({ _id, name, full_name, profile_picture }, idx) => {
            const displayName =
              full_name && full_name !== "" && full_name !== "-"
                ? full_name
                : name;
            const profilePicUrl =
              profile_picture?.url_compressed ||
              (profile_picture?.url &&
              profile_picture.url !== "-" &&
              profile_picture.url !== "default"
                ? profile_picture.url
                : pic_static);

            let user_unit;
            if (members_group_id.includes(_id)) {
              user_unit = (
                <main
                  style={{
                    textAlign: "left",
                    marginBottom: "10px",
                    cursor: "default",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={profilePicUrl}
                    alt={displayName}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #2196f3",
                      backgroundColor: "#c1c1c1",
                    }}
                  />
                  <section
                    style={{
                      marginLeft: "10px",
                      fontSize: "12px",
                    }}
                  >
                    {displayName}
                  </section>
                  <img
                    src={checklist}
                    alt="check"
                    style={{
                      marginLeft: "5px",
                      width: "15px",
                      height: "15px",
                    }}
                  />
                </main>
              );
            } else {
              user_unit = (
                <main
                  style={{
                    textAlign: "left",
                    marginBottom: "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => onAddMember(_id)}
                  data-mapid="clickAdd"
                >
                  <img
                    src={profilePicUrl}
                    alt={displayName}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #2196f3",
                      backgroundColor: "#c1c1c1",
                    }}
                  />
                  <section
                    style={{
                      marginLeft: "10px",
                      fontSize: "12px",
                    }}
                  >
                    {displayName}
                  </section>
                </main>
              );
            }
            return (
              <main key={idx} className="card_search_group">
                {user_unit}
              </main>
            );
          })}
        </section>
      );
    }

    const searchForm = (
      <main className="group_search">
        <div className="group_search_row">
          <div className="group_search_input">
            <img src={icon_search} alt="search" height="16px" />
            <input
              type="text"
              placeholder={dict["Search user to add as member"][language]}
              autoComplete="off"
              name="search_user"
              value={search_user}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              data-mapid="inputUser"
            />
            {user_list.length > 0 && (
              <div
                className="button_small background_red"
                onClick={handleResetSearchUser}
                style={{ cursor: "pointer" }}
              >
                X
              </div>
            )}
          </div>
          {search_user?.length !== 0 && (
            <main
              style={{
                maxHeight: "300px",
                width: "75%",
                overflowY: "auto",
                backgroundColor: "#fff",
                padding: "10px 20px",
                position: "absolute",
                zIndex: 1,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              <section>{user_list_content}</section>
            </main>
          )}
        </div>
      </main>
    );

    let memberStatus = "viewer";
    if (group?._id) {
      memberStatus = group?.members?.find(
        (m) => m?.user?._id === user?._id,
      )?.status;
    }

    itemContent = (
      <main style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <section>
          <Link to={`/user_group/group`}>
            <button className="button_big background_black  margin_right">
              <ICON_ARROW_LEFT_LINE /> {dict["Back"][language]}
            </button>
          </Link>

          <h1 className="text_big" style={{ marginTop: "10px" }}>
            {group?.name}
          </h1>

          <p className="subtitle_detail_group">
            {group?.members?.length} members
          </p>
        </section>

        {(memberStatus === "creator" || memberStatus === "admin") && searchForm}

        {/* --- FIXED TABLE LAYOUT START --- */}
        <section
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #eaeaea",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            width: "100%",
            marginTop: "20px",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
                tableLayout: "fixed",
              }}
            >
              <thead style={{ backgroundColor: "#f9f9f9" }}>
                <tr>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      color: "#666",
                      fontWeight: "600",
                      width: "50px", // Fixed width
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={resetSort}
                    title="Click to reset sort order"
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      No.
                      {sortConfig.key === null && (
                        <span style={{ marginLeft: "5px", display: "flex" }}>
                          <SortIconUp />
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={{ width: "60px" }}></th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      color: "#666",
                      fontWeight: "600",
                      width: "auto",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("name")}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      Name & Email
                      {sortConfig.key === "name" && (
                        <span style={{ marginLeft: "5px", display: "flex" }}>
                          {sortConfig.direction === "asc" ? (
                            <SortIconUp />
                          ) : (
                            <SortIconDown />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      color: "#666",
                      fontWeight: "600",
                      width: "150px",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("role")}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      Role
                      {sortConfig.key === "role" && (
                        <span style={{ marginLeft: "5px", display: "flex" }}>
                          {sortConfig.direction === "asc" ? (
                            <SortIconUp />
                          ) : (
                            <SortIconDown />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      color: "#666",
                      fontWeight: "600",
                      width: "80px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted_members.map((m, idx) => {
                  const member_user = m?.user;
                  const status = m?.status;
                  const profile_picture = member_user?.profile_picture;

                  if (!member_user) return null;

                  const member_id = m?._id;
                  const _id = member_user?._id;
                  const fullName = member_user.full_name || member_user.name;
                  const email = member_user.email;
                  const statusArray = ["admin", "contributor", "viewer"];

                  const profilePicUrl =
                    profile_picture?.url_compressed ||
                    (profile_picture?.url &&
                    profile_picture.url !== "-" &&
                    profile_picture.url !== "default"
                      ? profile_picture.url
                      : pic_static);

                  // Status Button Logic
                  let statusButton;
                  if (status === "creator") {
                    statusButton = (
                      <span
                        style={{
                          fontWeight: "500",
                          padding: "4px 8px",
                          backgroundColor: "#eee",
                          borderRadius: "4px",
                          fontSize: "12px",
                          display: "inline-block",
                        }}
                      >
                        {m?.status}
                      </span>
                    );
                  } else if (
                    memberStatus === "creator" ||
                    memberStatus === "admin"
                  ) {
                    let status_will_show = status;
                    if (status === "pending" || status === "pending_view") {
                      status_will_show = "viewer";
                    }

                    statusButton = (
                      <select
                        id={_id}
                        name={member_id}
                        value={status_will_show}
                        onChange={handleEditMember}
                        data-mapid="pilihStatus"
                        style={{
                          padding: "4px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          width: "100%", // Fill the 150px cell
                          maxWidth: "140px",
                        }}
                      >
                        {statusArray.map((s, idxStatus) => (
                          <option key={idxStatus} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    );
                  } else {
                    statusButton = (
                      <span
                        style={{
                          color: "#555",
                          fontWeight: "500",
                        }}
                      >
                        {status}
                      </span>
                    );
                  }

                  // Delete Button Logic
                  let deleteButton = null;
                  if (
                    (memberStatus === "creator" || memberStatus === "admin") &&
                    status !== "creator" &&
                    email !== user?.email
                  ) {
                    deleteButton = (
                      <button
                        onClick={() => toggleDeleteMember(m)}
                        className="button_very_small background_red"
                        data-mapid="clickDelete"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {status === "creator"
                          ? dict["Leave"][language]
                          : dict["Kick"][language]}
                      </button>
                    );
                  }

                  return (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* 1. Index (Fixed 50px) */}
                      <td
                        style={{
                          padding: "8px 12px",
                          whiteSpace: "nowrap",
                          color: "#888",
                        }}
                      >
                        {parseInt(idx + 1)}
                      </td>

                      {/* 2. Avatar (Fixed 60px) */}
                      <td
                        style={{
                          padding: "8px 12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <img
                          src={profilePicUrl}
                          alt={fullName}
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            display: "block",
                            border: "1px solid #eee",
                          }}
                        />
                      </td>

                      {/* 3. Name & Email (Auto + Truncate) */}
                      <td
                        style={{
                          padding: "8px 12px",
                          overflow: "hidden", // Crucial for fixed layout truncation
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontWeight: "600",
                              color: "#333",
                              marginRight: "8px",
                            }}
                            title={fullName} // Tooltip on hover
                          >
                            {fullName || "-"}
                          </div>
                          <span
                            style={{
                              fontSize: "10px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              backgroundColor:
                                m?.active_status === "active"
                                  ? "#e8f5e9"
                                  : "#f5f5f5",
                              color:
                                m?.active_status === "active"
                                  ? "#2e7d32"
                                  : "#9e9e9e",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m?.active_status}
                          </span>
                        </div>
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            color: "#999",
                          }}
                          title={email}
                        >
                          {email}
                        </div>
                      </td>

                      {/* 4. Role (Fixed 150px) */}
                      <td
                        style={{
                          padding: "8px 12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {statusButton}
                      </td>

                      {/* 5. Action (Fixed 80px) */}
                      <td
                        style={{
                          padding: "8px 12px",
                          whiteSpace: "nowrap",
                          textAlign: "right",
                        }}
                      >
                        {deleteButton}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        {/* --- FIXED TABLE LAYOUT END --- */}
      </main>
    );
  }

  const modalDeleteMemberContent = modalDeleteMember && (
    <Modal
      modalSize="small"
      isOpen={modalDeleteMember}
      onClose={() => toggleDeleteMember(member)}
    >
      <div className="box-body" style={{ textAlign: "center" }}>
        <div className="text_medium">
          {dict["Are you sure to delete"][language]}
        </div>
        <br />
        <div className="text_bold" style={{ textAlign: "center" }}>
          {member?.user?.full_name
            ? member?.user?.full_name
            : member?.user?.name}
        </div>
        <br />
        <div
          onClick={handleDeleteMember}
          className="button_very_small background_red"
          data-mapid="clickDeleteModal"
        >
          {dict["Delete"][language]}
        </div>
      </div>
    </Modal>
  );

  return (
    <div className="main_container">
      <Helmet>
        <title>{`${
          group?.name ? group?.name : dict["Group"][language]
        }`}</title>
        <meta
          name="description"
          content={`${group?.name ? group?.name : dict["Group"][language]}`}
        />
      </Helmet>

      {itemContent}
      {modalDeleteMemberContent}
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  license_reducer: state.license_reducer,
});

export default connect(mapStateToProps, {
  getGroup,
  deleteMember,
  editMember,
  searchUser,
  resetSearchUser,
  addMember,
  invitations,
  set_value_user,
  set_value_project,
  searchCollaborator,
  is_pass_license_sharing,
  get_payment_id_from_license,
  block_license_with_handler,
})(GroupDetail);
